import { createTheme } from "@mui/material/styles";
import { colors, fonts, breakpoints as bp, tint } from "./theme/tokens";

const SEMANTIC_BUTTON_COLORS = new Set(["error", "warning", "info", "success"]);

function outlinedThirdBrand() {
  const main = colors.outlinedBrandMain;
  const hover = colors.outlinedBrandHover;
  if (!main || !hover) return null;

  return {
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: main,
    color: colors.textPrimary,
    backgroundColor: "transparent",
    "&:hover": {
      borderColor: hover,
      backgroundColor: tint(main, 0.14),
    },
    "&.Mui-disabled": {
      borderColor: tint(main, 0.45),
      color: colors.textMuted,
    },
  };
}

const thirdBrandOutlinedStyle = outlinedThirdBrand();
const thirdBrandOutlinedVariants = thirdBrandOutlinedStyle
  ? [
      {
        props: { variant: "outlined", color: "primary" },
        style: thirdBrandOutlinedStyle,
      },
      {
        props: { variant: "outlined", color: "secondary" },
        style: thirdBrandOutlinedStyle,
      },
    ]
  : [];

const theme = createTheme({
  breakpoints: {
    values: { ...bp },
  },
  palette: {
    primary: {
      main: colors.primary,
    },
    secondary: {
      main: colors.secondary,
      dark: colors.secondaryHover,
      contrastText: colors.textPrimary,
    },
    accent: {
      main: colors.accent,
      secondary: colors.accentHover,
    },
    highlight: {
      main: colors.highlight,
      dark: colors.highlightHover,
      contrastText: colors.textPrimary,
    },
    error: { main: colors.error },
    warning: { main: colors.warning },
    success: { main: colors.success },
    info: { main: colors.info },
    surfaces: {
      footer: colors.brandSurface,
      navbar: colors.brandSurface,
      drawer: colors.brandSurface,
      section: colors.sectionTint,
      sections: colors.sectionTints,
      onDark: colors.onBrandSurface,
      divider: colors.footerBorder,
      dividerOnDark:
        colors.onBrandSurface === "#FFFFFF"
          ? "rgba(255,255,255,0.15)"
          : "rgba(0,0,0,0.12)",
    },
  },
  typography: {
    fontFamily: fonts.body,
    h1: {
      fontFamily: fonts.heading,
      fontWeight: 600,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontFamily: fonts.heading,
      fontWeight: 600,
      letterSpacing: "-0.02em",
    },
    h3: {
      fontFamily: fonts.heading,
      fontWeight: 600,
      letterSpacing: "-0.015em",
    },
    h4: {
      fontFamily: fonts.heading,
      fontWeight: 600,
      letterSpacing: "-0.01em",
    },
    h5: {
      fontFamily: fonts.heading,
      fontWeight: 600,
    },
    h6: {
      fontFamily: fonts.heading,
      fontWeight: 600,
    },
    subtitle1: { fontWeight: 600 },
    button: {
      fontFamily: fonts.body,
      textTransform: "none",
      fontWeight: 600,
      letterSpacing: 0,
    },
    overline: {
      letterSpacing: "0.18em",
      fontWeight: 700,
      color: colors.primary,
      textTransform: "uppercase",
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: false,
      },
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 700,
          borderRadius: 8,
        },
        contained: ({ ownerState, theme }) => {
          if (SEMANTIC_BUTTON_COLORS.has(ownerState.color)) return {};
          if (
            ownerState.color === "secondary" &&
            colors.secondaryBrandAccent
          ) {
            return {
              paddingInline: 24,
              paddingBlock: 10,
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.secondary.contrastText,
              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
              "&:hover": {
                backgroundColor: theme.palette.secondary.dark,
                boxShadow: "0 6px 16px rgba(0,0,0,0.18)",
              },
            };
          }
          return {
            paddingInline: 24,
            paddingBlock: 10,
            backgroundColor: colors.accent,
            color: colors.textPrimary,
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            "&:hover": {
              backgroundColor: colors.accentHover,
              boxShadow: "0 6px 16px rgba(0,0,0,0.18)",
            },
          };
        },
      },
      variants: thirdBrandOutlinedVariants,
    },
  },
});

export default theme;
